import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Clock, Radio, Lock, CheckCircle2, Users, Trophy, Flame, Star, Send, ChevronLeft, AlertCircle, Tv } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API_URL } from '../config/api';

const STATE_COLORS = {
  UPCOMING: { bg: 'bg-blue-900/30', border: 'border-blue-700/40', text: 'text-blue-400', label: 'Upcoming' },
  PRE_LIVE: { bg: 'bg-amber-900/30', border: 'border-amber-700/40', text: 'text-amber-400', label: 'Pre-Live' },
  LIVE: { bg: 'bg-red-900/30', border: 'border-red-500/60', text: 'text-red-400', label: 'LIVE' },
  LOCKED: { bg: 'bg-orange-900/30', border: 'border-orange-700/40', text: 'text-orange-400', label: 'Locked' },
  COMPLETE: { bg: 'bg-green-900/30', border: 'border-green-700/40', text: 'text-green-400', label: 'Complete' },
};

const STATE_ICONS = {
  UPCOMING: Clock,
  PRE_LIVE: AlertCircle,
  LIVE: Radio,
  LOCKED: Lock,
  COMPLETE: CheckCircle2,
};

const StateBadge = ({ state }) => {
  const colors = STATE_COLORS[state] || STATE_COLORS.UPCOMING;
  const Icon = STATE_ICONS[state] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors.bg} ${colors.border} ${colors.text} border`} data-testid={`state-badge-${state}`}>
      <Icon className="h-3 w-3" />
      {state === 'LIVE' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
      {colors.label}
    </span>
  );
};

const Countdown = ({ airDatetime }) => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const air = new Date(airDatetime);
      const diff = air - now;
      if (diff <= 0) { setTimeLeft('Now'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (d > 0) setTimeLeft(`${d}d ${h}h ${m}m`);
      else if (h > 0) setTimeLeft(`${h}h ${m}m ${s}s`);
      else setTimeLeft(`${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [airDatetime]);
  return <span className="font-mono text-amber-400 text-sm font-bold" data-testid="countdown-timer">{timeLeft}</span>;
};

const RealityHub = ({ user, isOpen, onClose }) => {
  const [view, setView] = useState('hub');
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('predict');

  // Prediction state
  const [myPrediction, setMyPrediction] = useState(null);
  const [allPredictions, setAllPredictions] = useState([]);
  const [submittingPrediction, setSubmittingPrediction] = useState(false);

  // Voting state
  const [myVote, setMyVote] = useState(null);
  const [voteResults, setVoteResults] = useState(null);
  const [submittingVote, setSubmittingVote] = useState(false);

  // Drama rating
  const [dramaData, setDramaData] = useState(null);
  const [myDramaRating, setMyDramaRating] = useState(0);
  const [submittingDrama, setSubmittingDrama] = useState(false);

  // Discussion
  const [discussion, setDiscussion] = useState({ is_active: false, messages: [] });
  const [newMessage, setNewMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const msgEndRef = useRef(null);

  // Leaderboard
  const [leaderboard, setLeaderboard] = useState([]);

  const fetchEpisodes = useCallback(async () => {
    setLoading(true);
    try {
      const [liveRes, upcomingRes, completedRes] = await Promise.all([
        axios.get(`${API_URL}/api/reality-hub/live`),
        axios.get(`${API_URL}/api/reality-hub/upcoming?limit=10`),
        axios.get(`${API_URL}/api/reality-hub/episodes?state=COMPLETE&limit=10`),
      ]);
      const all = [
        ...liveRes.data.map(e => ({ ...e, _sort: 0 })),
        ...upcomingRes.data.map(e => ({ ...e, _sort: 1 })),
        ...completedRes.data.map(e => ({ ...e, _sort: 2 })),
      ];
      setEpisodes(all);
    } catch (e) { console.error('Fetch episodes error:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reality-hub/leaderboard`);
      setLeaderboard(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchEpisodes();
      fetchLeaderboard();
      setView('hub');
      setSelectedEpisode(null);
    }
  }, [isOpen, fetchEpisodes, fetchLeaderboard]);

  const openEpisode = async (ep) => {
    try {
      const res = await axios.get(`${API_URL}/api/reality-hub/episodes/${ep.id}`);
      setSelectedEpisode(res.data);
      setView('episode');

      // Fetch user's prediction
      const predRes = await axios.get(`${API_URL}/api/reality-hub/predictions/${ep.id}?user_id=${user.id}`);
      setMyPrediction(predRes.data.length > 0 ? predRes.data[0] : null);

      // Fetch all predictions
      const allPredRes = await axios.get(`${API_URL}/api/reality-hub/predictions/${ep.id}`);
      setAllPredictions(allPredRes.data);

      // Fetch votes
      const voteRes = await axios.get(`${API_URL}/api/reality-hub/votes/${ep.id}`);
      setVoteResults(voteRes.data);

      // Fetch drama
      const dramaRes = await axios.get(`${API_URL}/api/reality-hub/drama-rating/${ep.id}`);
      setDramaData(dramaRes.data);

      // Fetch discussion
      const discRes = await axios.get(`${API_URL}/api/reality-hub/discussion/${ep.id}`);
      setDiscussion(discRes.data);

      // Set active tab based on state
      const state = res.data.state;
      if (state === 'PRE_LIVE') setActiveTab('predict');
      else if (state === 'LIVE') setActiveTab('discuss');
      else if (state === 'COMPLETE') setActiveTab('drama');
      else setActiveTab('predict');
    } catch (e) { console.error('Open episode error:', e); }
  };

  const submitPrediction = async (contestantId) => {
    if (!selectedEpisode || myPrediction) return;
    setSubmittingPrediction(true);
    try {
      const res = await axios.post(`${API_URL}/api/reality-hub/prediction?user_id=${user.id}&episode_id=${selectedEpisode.id}&contestant_id=${contestantId}`);
      setMyPrediction(res.data.prediction);
      const allRes = await axios.get(`${API_URL}/api/reality-hub/predictions/${selectedEpisode.id}`);
      setAllPredictions(allRes.data);
    } catch (e) { console.error('Prediction error:', e); }
    finally { setSubmittingPrediction(false); }
  };

  const submitVote = async (contestantId) => {
    if (!selectedEpisode) return;
    setSubmittingVote(true);
    try {
      await axios.post(`${API_URL}/api/reality-hub/vote?user_id=${user.id}&episode_id=${selectedEpisode.id}&contestant_id=${contestantId}`);
      setMyVote(contestantId);
      const res = await axios.get(`${API_URL}/api/reality-hub/votes/${selectedEpisode.id}`);
      setVoteResults(res.data);
    } catch (e) { console.error('Vote error:', e); }
    finally { setSubmittingVote(false); }
  };

  const submitDrama = async () => {
    if (!selectedEpisode || myDramaRating === 0) return;
    setSubmittingDrama(true);
    try {
      await axios.post(`${API_URL}/api/reality-hub/drama-rating?user_id=${user.id}&episode_id=${selectedEpisode.id}&rating=${myDramaRating}`);
      const res = await axios.get(`${API_URL}/api/reality-hub/drama-rating/${selectedEpisode.id}`);
      setDramaData(res.data);
    } catch (e) { console.error('Drama rating error:', e); }
    finally { setSubmittingDrama(false); }
  };

  const sendMessage = async () => {
    if (!selectedEpisode || !newMessage.trim()) return;
    setSendingMsg(true);
    try {
      await axios.post(`${API_URL}/api/reality-hub/discussion/${selectedEpisode.id}?user_id=${user.id}&content=${encodeURIComponent(newMessage.trim())}`);
      setNewMessage('');
      const res = await axios.get(`${API_URL}/api/reality-hub/discussion/${selectedEpisode.id}`);
      setDiscussion(res.data);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { console.error('Send message error:', e); }
    finally { setSendingMsg(false); }
  };

  // Auto-refresh discussion during LIVE
  useEffect(() => {
    if (!selectedEpisode || selectedEpisode.state !== 'LIVE' || activeTab !== 'discuss') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reality-hub/discussion/${selectedEpisode.id}`);
        setDiscussion(res.data);
      } catch (e) { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedEpisode, activeTab]);

  if (!isOpen) return null;

  const getInitials = (fn, ln) => `${fn?.[0] || ''}${ln?.[0] || ''}`.toUpperCase();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-zinc-900 text-white border-zinc-700 max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Reality Hub</DialogTitle>
        <DialogDescription className="sr-only">Reality TV shows live engagement hub</DialogDescription>

        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 z-10 px-4 pt-4 pb-3 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {view !== 'hub' && (
                <button onClick={() => { setView('hub'); setSelectedEpisode(null); }} className="text-gray-400 hover:text-white" data-testid="reality-back-btn">
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              <Radio className="h-5 w-5 text-red-500" />
              <h2 className="text-base font-bold">
                {view === 'hub' ? 'Reality Hub' : view === 'leaderboard' ? 'Predictions Leaderboard' : selectedEpisode?.show_name || 'Episode'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {view === 'hub' && (
                <button onClick={() => setView('leaderboard')} className="p-1.5 rounded-lg hover:bg-zinc-700 text-amber-400" data-testid="reality-leaderboard-btn">
                  <Trophy className="h-4 w-4" />
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="reality-close-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-3">
          {/* === HUB VIEW === */}
          {view === 'hub' && (
            loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin h-6 w-6 border-2 border-red-500 border-t-transparent rounded-full" />
              </div>
            ) : episodes.length === 0 ? (
              <div className="text-center py-10">
                <Tv className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No reality episodes tracked yet</p>
                <p className="text-gray-600 text-xs mt-1">Episodes will appear here from the TVMaze countdown feed</p>
              </div>
            ) : (
              <div className="space-y-2">
                {episodes.map(ep => (
                  <button
                    key={ep.id}
                    onClick={() => openEpisode(ep)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors hover:bg-zinc-800 ${STATE_COLORS[ep.state]?.border || 'border-zinc-700'}`}
                    data-testid={`episode-card-${ep.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-semibold truncate">{ep.show_name || ep.show?.name || 'Unknown Show'}</p>
                        <p className="text-gray-400 text-xs">S{ep.season_number}E{ep.episode_number} &middot; {ep.title}</p>
                      </div>
                      <StateBadge state={ep.state} />
                    </div>
                    {(ep.state === 'UPCOMING' || ep.state === 'PRE_LIVE') && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        <Countdown airDatetime={ep.air_datetime} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )
          )}

          {/* === EPISODE VIEW === */}
          {view === 'episode' && selectedEpisode && (
            <div>
              {/* Episode header */}
              <div className={`rounded-lg p-3 mb-4 border ${STATE_COLORS[selectedEpisode.state]?.bg} ${STATE_COLORS[selectedEpisode.state]?.border}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white font-bold text-sm">{selectedEpisode.show_name}</p>
                  <StateBadge state={selectedEpisode.state} />
                </div>
                <p className="text-gray-400 text-xs">S{selectedEpisode.season_number}E{selectedEpisode.episode_number}: {selectedEpisode.title}</p>
                {(selectedEpisode.state === 'UPCOMING' || selectedEpisode.state === 'PRE_LIVE') && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-amber-400" />
                    <Countdown airDatetime={selectedEpisode.air_datetime} />
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex border-b border-zinc-700 mb-4 -mx-4 px-4 overflow-x-auto">
                {[
                  { key: 'predict', label: 'Predict', enabled: ['PRE_LIVE', 'LIVE', 'LOCKED', 'COMPLETE'].includes(selectedEpisode.state) },
                  { key: 'vote', label: 'Vote', enabled: ['LIVE', 'LOCKED', 'COMPLETE'].includes(selectedEpisode.state) },
                  { key: 'discuss', label: 'Discuss', enabled: ['PRE_LIVE', 'LIVE', 'LOCKED', 'COMPLETE'].includes(selectedEpisode.state) },
                  { key: 'drama', label: 'Drama Meter', enabled: selectedEpisode.state === 'COMPLETE' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => tab.enabled && setActiveTab(tab.key)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === tab.key ? 'border-red-500 text-red-400' :
                      tab.enabled ? 'border-transparent text-gray-400 hover:text-white' :
                      'border-transparent text-zinc-600 cursor-not-allowed'
                    }`}
                    disabled={!tab.enabled}
                    data-testid={`tab-${tab.key}`}
                  >
                    {tab.label}
                    {tab.key === 'discuss' && discussion.messages.length > 0 && (
                      <span className="ml-1 text-[10px] bg-zinc-700 px-1 rounded">{discussion.messages.length}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* === PREDICT TAB === */}
              {activeTab === 'predict' && (
                <div>
                  <p className="text-gray-400 text-xs mb-3 font-medium uppercase tracking-wide">
                    {myPrediction ? 'Your Prediction' : 'Who will be eliminated?'}
                  </p>

                  {selectedEpisode.state === 'LOCKED' && !myPrediction && (
                    <div className="bg-orange-900/20 border border-orange-700/40 rounded-lg p-3 mb-3 flex items-center gap-2">
                      <Lock className="h-4 w-4 text-orange-400 flex-shrink-0" />
                      <p className="text-orange-300 text-xs">Predictions are locked for this episode</p>
                    </div>
                  )}

                  {/* Result banner if complete */}
                  {selectedEpisode.state === 'COMPLETE' && selectedEpisode.result && (
                    <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3 mb-3">
                      <p className="text-red-400 text-xs font-medium uppercase">Eliminated</p>
                      <p className="text-white font-bold text-sm">{selectedEpisode.result.eliminated_contestant_name}</p>
                      {myPrediction && (
                        <p className={`text-xs mt-1 font-medium ${myPrediction.is_correct ? 'text-green-400' : 'text-gray-400'}`}>
                          {myPrediction.is_correct ? `Correct! +${myPrediction.points_awarded} pts` : 'Incorrect prediction'}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    {(selectedEpisode.contestants || []).filter(c => c.status === 'active' || (selectedEpisode.result?.eliminated_contestant_id === c.id)).map(contestant => {
                      const isPicked = myPrediction?.contestant_id === contestant.id;
                      const canPick = !myPrediction && (selectedEpisode.state === 'PRE_LIVE' || selectedEpisode.state === 'LIVE');
                      const predCount = allPredictions.filter(p => p.contestant_id === contestant.id).length;

                      return (
                        <button
                          key={contestant.id}
                          onClick={() => canPick && submitPrediction(contestant.id)}
                          disabled={!canPick || submittingPrediction}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                            isPicked ? 'border-red-500 bg-red-500/10' :
                            canPick ? 'border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800' :
                            'border-zinc-800 opacity-60 cursor-default'
                          }`}
                          data-testid={`predict-contestant-${contestant.id}`}
                        >
                          <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                            {contestant.image_url ? <img src={contestant.image_url} alt="" className="w-full h-full object-cover" /> : contestant.name[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isPicked ? 'text-red-400' : 'text-white'}`}>{contestant.name}</p>
                            {predCount > 0 && <p className="text-gray-500 text-[10px]">{predCount} prediction{predCount !== 1 ? 's' : ''}</p>}
                          </div>
                          {isPicked && <CheckCircle2 className="h-4 w-4 text-red-400 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* === VOTE TAB === */}
              {activeTab === 'vote' && (
                <div>
                  <p className="text-gray-400 text-xs mb-3 font-medium uppercase tracking-wide">Vote for your favorite</p>

                  {selectedEpisode.state !== 'LIVE' && (
                    <div className="bg-zinc-800 rounded-lg p-3 mb-3 text-center">
                      <p className="text-gray-400 text-xs">{selectedEpisode.state === 'COMPLETE' ? 'Voting has ended' : 'Voting opens during the LIVE broadcast'}</p>
                    </div>
                  )}

                  <div className="space-y-2 mb-4">
                    {(selectedEpisode.contestants || []).filter(c => c.status === 'active').map(contestant => {
                      const isVoted = myVote === contestant.id;
                      const canVote = selectedEpisode.state === 'LIVE';
                      const voteCount = voteResults?.results?.find(r => r.contestant_id === contestant.id)?.count || 0;
                      const totalVotes = voteResults?.total_votes || 0;
                      const pct = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;

                      return (
                        <button
                          key={contestant.id}
                          onClick={() => canVote && submitVote(contestant.id)}
                          disabled={!canVote || submittingVote}
                          className={`w-full p-3 rounded-lg border transition-colors text-left ${
                            isVoted ? 'border-purple-500 bg-purple-500/10' :
                            canVote ? 'border-zinc-700 hover:border-zinc-500' :
                            'border-zinc-800 cursor-default'
                          }`}
                          data-testid={`vote-contestant-${contestant.id}`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                              {contestant.image_url ? <img src={contestant.image_url} alt="" className="w-full h-full object-cover" /> : contestant.name[0]}
                            </div>
                            <p className={`text-sm font-medium flex-1 ${isVoted ? 'text-purple-400' : 'text-white'}`}>{contestant.name}</p>
                            <span className="text-xs text-gray-400">{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${isVoted ? 'bg-purple-500' : 'bg-zinc-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {voteResults && (
                    <p className="text-center text-gray-500 text-xs">{voteResults.total_votes} total vote{voteResults.total_votes !== 1 ? 's' : ''}</p>
                  )}
                </div>
              )}

              {/* === DISCUSS TAB === */}
              {activeTab === 'discuss' && (
                <div className="flex flex-col" style={{ minHeight: '300px' }}>
                  <div className="flex-1 space-y-2 mb-3 max-h-[40vh] overflow-y-auto">
                    {discussion.messages.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      discussion.messages.map(msg => (
                        <div key={msg.id} className="flex gap-2" data-testid={`msg-${msg.id}`}>
                          <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 overflow-hidden">
                            {msg.user?.profile_picture ? (
                              <img src={msg.user.profile_picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                              getInitials(msg.user?.first_name, msg.user?.last_name)
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs">
                              <span className="text-white font-medium">{msg.user?.display_name || `${msg.user?.first_name || ''} ${msg.user?.last_name || ''}`.trim() || 'User'}</span>
                              <span className="text-gray-600 ml-1.5">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </p>
                            <p className="text-gray-300 text-sm">{msg.content}</p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={msgEndRef} />
                  </div>

                  {selectedEpisode.state !== 'UPCOMING' && (
                    <div className="flex gap-2 pt-2 border-t border-zinc-700">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        placeholder="Say something..."
                        className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm flex-1"
                        data-testid="discussion-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sendingMsg}
                        className="bg-red-600 hover:bg-red-700 h-9 px-3"
                        data-testid="discussion-send-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* === DRAMA METER TAB === */}
              {activeTab === 'drama' && (
                <div>
                  <p className="text-gray-400 text-xs mb-4 font-medium uppercase tracking-wide">Rate the drama level</p>

                  {selectedEpisode.state !== 'COMPLETE' ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">Drama Meter opens after the episode ends</p>
                    </div>
                  ) : (
                    <>
                      {/* Average drama gauge */}
                      {dramaData && dramaData.count > 0 && (
                        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl p-4 mb-4 border border-red-800/30 text-center" data-testid="drama-avg">
                          <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">{dramaData.average}</p>
                          <p className="text-gray-400 text-xs mt-1">{dramaData.count} rating{dramaData.count !== 1 ? 's' : ''}</p>
                          <div className="flex justify-center gap-1 mt-2">
                            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                              <div key={n} className={`w-4 h-1 rounded-full ${n <= Math.round(dramaData.average) ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-zinc-700'}`} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* User rating input */}
                      <p className="text-gray-400 text-xs mb-2">Your Rating</p>
                      <div className="flex gap-1 mb-3">
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <button
                            key={n}
                            onClick={() => setMyDramaRating(n)}
                            className={`flex-1 h-10 rounded-lg border text-xs font-bold transition-all ${
                              myDramaRating >= n
                                ? 'bg-gradient-to-b from-red-600 to-orange-600 border-red-500 text-white'
                                : 'bg-zinc-800 border-zinc-700 text-gray-500 hover:border-zinc-500'
                            }`}
                            data-testid={`drama-btn-${n}`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <Button
                        onClick={submitDrama}
                        disabled={myDramaRating === 0 || submittingDrama}
                        className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white h-10"
                        data-testid="drama-submit-btn"
                      >
                        {submittingDrama ? 'Submitting...' : `Rate Drama: ${myDramaRating}/10`}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === LEADERBOARD VIEW === */}
          {view === 'leaderboard' && (
            <div>
              <p className="text-gray-400 text-xs mb-3 font-medium uppercase tracking-wide">Prediction Rankings</p>
              {leaderboard.length === 0 ? (
                <div className="text-center py-10">
                  <Trophy className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No predictions scored yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        entry.rank <= 3 ? 'bg-amber-900/20 border-amber-700/40' : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid={`lb-entry-${entry.rank}`}
                    >
                      <span className={`text-sm font-black w-6 text-center ${
                        entry.rank === 1 ? 'text-amber-400' : entry.rank === 2 ? 'text-gray-300' : entry.rank === 3 ? 'text-orange-400' : 'text-gray-500'
                      }`}>#{entry.rank}</span>
                      <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                        {entry.profile_picture ? <img src={entry.profile_picture} alt="" className="w-full h-full object-cover" /> : entry.display_name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{entry.display_name}</p>
                        <p className="text-gray-500 text-[10px]">{entry.correct_predictions} correct</p>
                      </div>
                      <span className="text-amber-400 font-bold text-sm">{entry.total_points} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RealityHub;
